import { translate as t } from '../translations';

type SupportedLanguage = 'fr' | 'en';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
const NHL_PLAYER_FUNCTION_PATH = '/functions/v1/nhl-player-landing';
const CACHE_TTL_MS = 1000 * 60 * 5; // 5 minutes

interface CacheEntry<T> {
  expiresAt: number;
  data: T;
}

const playerCache = new Map<number, CacheEntry<NhlPlayerLanding>>();

export interface NhlPlayerName {
  default: string;
  fr?: string;
}

export interface NhlPlayerLanding {
  playerId: number;
  isActive?: boolean;
  currentTeamId?: number;
  currentTeamAbbrev?: string;
  fullTeamName?: NhlPlayerName;
  teamCommonName?: NhlPlayerName;
  teamPlaceNameWithPreposition?: NhlPlayerName;
  firstName?: NhlPlayerName;
  lastName?: NhlPlayerName;
  badges?: Array<{
    logoUrl?: NhlPlayerName;
    title?: NhlPlayerName;
  }>;
  teamLogo?: string;
  sweaterNumber?: number;
  position?: string;
  headshot?: string;
  heroImage?: string;
  heightInInches?: number;
  heightInCentimeters?: number;
  weightInPounds?: number;
  weightInKilograms?: number;
  birthDate?: string;
  birthCity?: NhlPlayerName;
  birthStateProvince?: NhlPlayerName;
  birthCountry?: string;
  shootsCatches?: 'L' | 'R' | 'C';
  draftDetails?: {
    year?: number;
    teamAbbrev?: string;
    round?: number;
    pickInRound?: number;
    overallPick?: number;
  };
  playerSlug?: string;
  featuredStats?: NhlFeaturedStats;
  careerTotals?: {
    regularSeason?: Record<string, number | string> & { avgToi?: string; faceoffWinningPctg?: number };
    playoffs?: Record<string, number | string> & { avgToi?: string; faceoffWinningPctg?: number };
  };
  last5Games?: NhlLastGame[];
  seasonTotals?: Array<Record<string, unknown>>;
  awards?: Array<Record<string, unknown>>;
  currentTeamRoster?: Array<{
    playerId: number;
    lastName?: NhlPlayerName;
    firstName?: NhlPlayerName;
    playerSlug?: string;
  }>;
}

export interface NhlFeaturedStats {
  season?: number;
  regularSeason?: {
    subSeason?: Record<string, number>;
    career?: Record<string, number>;
  };
  playoffs?: {
    subSeason?: Record<string, number>;
    career?: Record<string, number>;
  };
}

export interface NhlSeasonStats {
  // Les données de /landing sont identiques à NhlPlayerLanding
  // Nous réutilisons la même structure
  playerId?: number;
  seasonTotals?: Array<{
    season: number;
    gameTypeId: number;
    leagueAbbrev?: string;
    sequenceNumber?: number;
    gamesPlayed?: number;
    goals?: number;
    assists?: number;
    points?: number;
    plusMinus?: number;
    powerPlayGoals?: number;
    powerPlayPoints?: number;
    shorthandedGoals?: number;
    shorthandedPoints?: number;
    gameWinningGoals?: number;
    shots?: number;
    shootingPctg?: number;
    pim?: number;
    avgToi?: string;
    faceoffWinningPctg?: number;
    // Stats gardien
    wins?: number;
    losses?: number;
    otLosses?: number;
    goalsAgainst?: number;
    goalsAgainstAvg?: number;
    savePctg?: number;
    shutouts?: number;
    shotsAgainst?: number;
    saves?: number;
  }>;
  // Toutes les autres propriétés de NhlPlayerLanding sont disponibles
  [key: string]: any;
}

export interface NhlLastGame {
  assists?: number;
  decision?: string;
  goalsAgainst?: number;
  gameDate?: string;
  gameId?: number;
  gameTypeId?: number;
  goals?: number;
  homeRoadFlag?: string;
  opponentAbbrev?: string;
  pim?: number;
  plusMinus?: number;
  points?: number;
  powerPlayGoals?: number;
  savePctg?: number;
  saves?: number;
  shotsAgainst?: number;
  shifts?: number;
  shorthandedGoals?: number;
  shots?: number;
  teamAbbrev?: string;
  toi?: string;
}

export class NhlPlayerError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'NhlPlayerError';
  }
}

/**
 * Récupère les détails d'un joueur NHL via l'API publique.
 */
export async function fetchNhlPlayerLanding(playerId: number, language: SupportedLanguage = 'fr'): Promise<NhlPlayerLanding> {
  if (!Number.isInteger(playerId) || playerId <= 0) {
    throw new NhlPlayerError('Identifiant de joueur NHL invalide');
  }

  const cached = playerCache.get(playerId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);

  try {
    const supabaseUrl = SUPABASE_URL;
    const supabaseAnonKey = SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new NhlPlayerError('Configuration Supabase manquante pour récupérer les joueurs NHL');
    }

    const query = new URLSearchParams({
      playerId: String(playerId),
      language,
    });

    const response = await fetch(`${supabaseUrl}${NHL_PLAYER_FUNCTION_PATH}?${query.toString()}`, {
      signal: controller.signal,
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
    });

    if (!response.ok) {
      const errorText = await safeReadError(response);
      throw new NhlPlayerError(
        t('player.detail.fetch.error', language) || errorText || 'Impossible de charger le joueur',
        response.status
      );
    }

    const data = (await response.json()) as NhlPlayerLanding;
    playerCache.set(playerId, {
      data,
      expiresAt: Date.now() + CACHE_TTL_MS,
    });

    return data;
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NhlPlayerError(t('player.detail.fetch.timeout', language) || 'Délai dépassé', 408);
    }

    if (error instanceof NhlPlayerError) {
      throw error;
    }

    const message = error instanceof Error ? error.message : 'Erreur inconnue lors du chargement du joueur';
    throw new NhlPlayerError(message);
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Récupère les statistiques d'un joueur (toutes les saisons via /landing).
 */
export async function fetchNhlPlayerSeasonStats(
  playerId: number, 
  language: SupportedLanguage = 'fr'
): Promise<NhlSeasonStats> {
  if (!Number.isInteger(playerId) || playerId <= 0) {
    throw new NhlPlayerError('Identifiant de joueur NHL invalide');
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);

  try {
    const supabaseUrl = SUPABASE_URL;
    const supabaseAnonKey = SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new NhlPlayerError('Configuration Supabase manquante pour récupérer les joueurs NHL');
    }

    const query = new URLSearchParams({
      playerId: String(playerId),
      language,
    });

    const NHL_SEASON_STATS_PATH = '/functions/v1/nhl-player-season-stats';
    const response = await fetch(`${supabaseUrl}${NHL_SEASON_STATS_PATH}?${query.toString()}`, {
      signal: controller.signal,
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
      },
    });

    if (!response.ok) {
      const errorText = await safeReadError(response);
      throw new NhlPlayerError(
        t('player.detail.fetch.error', language) || errorText || 'Impossible de charger les statistiques',
        response.status
      );
    }

    const data = (await response.json()) as NhlSeasonStats;
    return data;
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new NhlPlayerError(t('player.detail.fetch.timeout', language) || 'Délai dépassé', 408);
    }

    if (error instanceof NhlPlayerError) {
      throw error;
    }

    const message = error instanceof Error ? error.message : 'Erreur inconnue lors du chargement des statistiques';
    throw new NhlPlayerError(message);
  } finally {
    clearTimeout(timeout);
  }
}

async function safeReadError(response: Response): Promise<string | undefined> {
  try {
    const text = await response.text();
    return text?.slice(0, 200);
  } catch {
    return undefined;
  }
}

/**
 * Génère la liste des saisons disponibles (les 5 dernières années)
 */
export function getAvailableSeasons(): Array<{ label: string; value: string; isCurrent: boolean }> {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // Mois de 1 à 12
  
  // La saison NHL commence en octobre (mois 10), donc si on est avant octobre,
  // la saison actuelle est celle qui a commencé l'année précédente
  const currentSeasonStartYear = currentMonth >= 10 ? currentYear : currentYear - 1;
  
  const seasons = [];
  
  for (let i = 0; i < 5; i++) {
    const startYear = currentSeasonStartYear - i;
    const endYear = startYear + 1;
    const seasonId = `${startYear}${endYear}`;
    const label = `${startYear}-${String(endYear).slice(-2)}`;
    
    seasons.push({
      label,
      value: seasonId,
      isCurrent: i === 0
    });
  }
  
  return seasons;
}
